/**
 * Per-cell movement-reach halos. Rendered during a card drag to
 * show the legal one-step destinations for the unit being moved.
 *
 * Position + scale mutated each frame by
 * GameBoard.updateMovementReachOverlays. Same per-frame projector
 * pattern as CellTargetOverlays. React only re-renders when the
 * set of cellKeys itself churns (e.g. drag started / ended).
 *
 * Visual: soft green halo by default. v1.111. When the dragged
 * card is a Minion playable ONLY thanks to a Harbinger cell-discount,
 * the matching cells (passed in `harbingerCellKeys`) get
 * `data-harbinger="true"` and the CSS rule swaps them to a purple
 * pulse, matching the hand card's purple aura. Dragon-portal marks
 * (Dragonlord's Lair / Caelestis, passed in `dragonCellKeys`) get
 * `data-dragon="true"` and swap to a teal pulse matching the portal.
 * Sir Perceval's remembered Genesis destination (passed in
 * `percevalCellKeys`) gets `data-perceval="true"` and an amber pulse.
 * a reminder of where he should enter, NOT a legal-move halo (the cell
 * renders even when it's out of reach).
 */
export default function MovementReachOverlay({ cellKeys, harbingerCellKeys, dragonCellKeys, townCellKeys, percevalCellKeys, registerRefs }) {
  const harbingerSet = harbingerCellKeys instanceof Set
    ? harbingerCellKeys
    : new Set(harbingerCellKeys || []);
  const townSet = new Set(townCellKeys || []);
  const dragonSet = dragonCellKeys instanceof Set
    ? dragonCellKeys
    : new Set(dragonCellKeys || []);
  const percevalSet = percevalCellKeys instanceof Set
    ? percevalCellKeys
    : new Set(percevalCellKeys || []);
  // Perceval's reminder cell may sit outside the reachable set, so
  // union it in. Otherwise an out-of-reach destination never renders.
  const keys = Array.from(new Set([...(cellKeys || []), ...percevalSet]));
  if (keys.length === 0) return null;
  return (
    <div aria-hidden="true">
      {keys.map((key) => (
        <Halo
          key={key}
          cellKey={key}
          registerRefs={registerRefs}
          harbinger={harbingerSet.has(key)}
          dragon={dragonSet.has(key)}
          town={townSet.has(key)}
          perceval={percevalSet.has(key)}
        />
      ))}
    </div>
  );
}

function Halo({ cellKey, registerRefs, harbinger, dragon, town, perceval }) {
  const setHalo = (el) => registerRefs(cellKey, el);
  return (
    <div
      ref={setHalo}
      className="movement-reach-halo"
      data-harbinger={harbinger ? 'true' : undefined}
      data-dragon={dragon ? 'true' : undefined}
      data-town={town ? 'true' : undefined}
      data-perceval={perceval ? 'true' : undefined}
    />
  );
}
